import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaTrophy, FaUsers, FaCalendarAlt, FaSearch } from 'react-icons/fa';
import styles from './AwardAssignmentModal.module.css';
import awardsAPI from '../../api/awards';
import { getAuth } from 'firebase/auth';
import { formatRole as formatRoleUtil } from '../../utils/roleFormatting';

const AwardEditModal = ({ 
  award,
  programId, 
  onClose, 
  onAwardUpdated
}) => {
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [notes, setNotes] = useState('');
  const [selectedEventId, setSelectedEventId] = useState('');
  const [events, setEvents] = useState([]);
  const [players, setPlayers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [eventSearchTerm, setEventSearchTerm] = useState('');
  const [isEventDropdownOpen, setIsEventDropdownOpen] = useState(false);
  const [isPlayerDropdownOpen, setIsPlayerDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Pre-fill form with existing award data
  useEffect(() => {
    if (award) {
      // Pre-fill recipients
      const formattedRecipients = (award.recipients || []).map(r => ({
        userId: r.userId || r._id,
        playerName: r.playerName,
        teamId: r.teamId,
        teamName: r.teamName,
        roleKey: r.roleKey,
        roleName: r.roleName
      }));
      setSelectedRecipients(formattedRecipients);
      
      // Pre-fill notes
      setNotes(award.notes || '');
      
      // Pre-fill event - check both _id and id fields
      const eventId = award.eventId?._id || award.eventId?.id || award.eventId || '';
      setSelectedEventId(eventId);
    }
  }, [award]);

  // Fetch events and players
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch events
        try {
          const eventsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/${programId}`);
          if (eventsResponse.ok) {
            const eventsData = await eventsResponse.json();
            setEvents(Array.isArray(eventsData) ? eventsData : []);
          }
        } catch (eventError) {
          console.error('Error fetching events:', eventError);
        }

        // Fetch teams first
        let teamsData = [];
        try {
          const teamsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/teams/program/${programId}`);
          if (teamsResponse.ok) {
            const teams = await teamsResponse.json();
            teamsData = Array.isArray(teams) ? teams : [teams];
          }
        } catch (teamError) {
          console.error('Error fetching teams:', teamError);
        }

        // Fetch players
        try {
          const playersResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/teams/program/${programId}/available-players`);
          if (playersResponse.ok) {
            const playersData = await playersResponse.json();
            
            if (playersData.availablePlayers && Array.isArray(playersData.availablePlayers)) {
              // Create team lookup map - use userId to match with availablePlayers
              const teamMap = {};
              teamsData.forEach(team => {
                if (team.players && Array.isArray(team.players)) {
                  team.players.forEach(player => {
                    // Use userId field to match with available players
                    const playerId = player.userId || player._id;
                    teamMap[playerId] = {
                      teamId: team._id,
                      teamName: team.name,
                      roleKey: player.roleKey || 'player'
                    };
                  });
                }
              });

              const transformedPlayers = playersData.availablePlayers.map(player => {
                const teamInfo = teamMap[player._id] || {};
                
                return {
                  userId: player._id,
                  firstName: player.firstName || 'Unknown',
                  lastName: player.lastName || 'Player',
                  fullName: player.fullName || `${player.firstName || ''} ${player.lastName || ''}`.trim(),
                  teamId: teamInfo.teamId || player.teamId,
                  teamName: teamInfo.teamName || player.teamName || 'No Team',
                  roleKey: teamInfo.roleKey || player.roleKey || 'player',
                  roleName: player.roleName
                };
              });
              setPlayers(transformedPlayers);
            }
          }
        } catch (playerError) {
          console.error('Error fetching players:', playerError);
        }
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (programId) {
      fetchData();
    }
  }, [programId]);

  // Format role names using data-driven utility (roles loaded from database)
  const formatRole = formatRoleUtil;

  const getSelectedEventDisplayText = () => {
    if (selectedEventId === '') {
      return 'Series Award (No specific event)';
    }
    
    const selectedEvent = events.find(event => event._id === selectedEventId);
    
    if (selectedEvent) {
      const eventDate = new Date(selectedEvent.dateTime?.start || selectedEvent.date);
      const formattedDate = eventDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
      const eventType = selectedEvent.type || 'Event';
      return `${selectedEvent.title} - ${formattedDate} - ${eventType}`;
    }
    
    return 'Select an event...';
  };

  const handleRecipientToggle = (player) => {
    setSelectedRecipients(prev => {
      const isSelected = prev.some(p => p.userId === player.userId);
      if (isSelected) {
        return prev.filter(p => p.userId !== player.userId);
      } else {
        return [...prev, {
          userId: player.userId,
          playerName: `${player.firstName} ${player.lastName}`,
          teamId: player.teamId,
          teamName: player.teamName,
          roleKey: player.roleKey,
          roleName: player.roleName
        }];
      }
    });
  };

  // Event dropdown handlers
  const handleEventSearchFocus = () => {
    setIsEventDropdownOpen(true);
  };

  const handleEventSearchChange = (e) => {
    setEventSearchTerm(e.target.value);
    setIsEventDropdownOpen(true);
  };

  const handleEventSelect = (eventId) => {
    setSelectedEventId(eventId);
    setIsEventDropdownOpen(false);
    setEventSearchTerm('');
  };

  const handleEventDropdownBlur = (e) => {
    const relatedTarget = e.relatedTarget;
    if (relatedTarget && relatedTarget.closest(`.${styles.dropdownContent}`)) {
      return;
    }
    setTimeout(() => {
      setIsEventDropdownOpen(false);
    }, 150);
  };

  const handleDropdownOptionClick = (eventId, e) => {
    e.preventDefault();
    e.stopPropagation();
    handleEventSelect(eventId);
  };

  // Player dropdown handlers
  const handlePlayerSearchFocus = () => {
    setIsPlayerDropdownOpen(true);
  };

  const handlePlayerSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setIsPlayerDropdownOpen(true);
  };

  const handlePlayerDropdownBlur = (e) => {
    const relatedTarget = e.relatedTarget;
    if (relatedTarget && relatedTarget.closest(`.${styles.dropdownContent}`)) {
      return;
    }
    setTimeout(() => {
      setIsPlayerDropdownOpen(false);
    }, 150);
  };

  const handlePlayerOptionClick = (player, e) => {
    e.preventDefault();
    e.stopPropagation();
    handleRecipientToggle(player);
  };

  const handleSubmit = async () => {
    if (selectedRecipients.length === 0) {
      setError('Please select at least one recipient.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const auth = getAuth();
      if (!auth.currentUser) {
        throw new Error('Authentication required');
      }
      const token = await auth.currentUser.getIdToken();

      const updateData = {
        eventId: selectedEventId || undefined,
        recipients: selectedRecipients,
        notes: notes.trim() || undefined
      };

      const cleanUpdateData = Object.fromEntries(
        Object.entries(updateData).filter(([_, value]) => value !== undefined)
      );

      await awardsAPI.updateAward(award._id, cleanUpdateData, token);
      
      if (onAwardUpdated) {
        onAwardUpdated();
      }
      
      onClose();
      
    } catch (error) {
      console.error('Error updating award:', error);
      setError(error.message || 'Failed to update award. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredPlayers = players.filter(player => {
    const fullName = `${player.firstName} ${player.lastName}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase());
  });

  const filteredEvents = events
    .filter(event => event.status === 'Completed') // Only show completed events
    .filter(event => {
      if (!eventSearchTerm) return true;
      const searchLower = eventSearchTerm.toLowerCase();
      const eventTitle = event.title?.toLowerCase() || '';
      const eventType = event.type?.toLowerCase() || '';
      const eventDate = new Date(event.dateTime?.start || event.date).toLocaleDateString().toLowerCase();
      return eventTitle.includes(searchLower) || eventType.includes(searchLower) || eventDate.includes(searchLower);
    });

  return createPortal(
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>
            <FaTrophy className={styles.modalTitleIcon} />
            Edit Award
          </div>
          <div className={styles.headerControls}>
            <button className={styles.closeButton} onClick={onClose} aria-label="Close">
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className={styles.modalBody}>
          <form className={styles.form}>
            {error && (
              <div className={styles.error}>
                {error}
              </div>
            )}

            {loading && (
              <div className={styles.loading}>
                <div className={styles.spinner}></div>
                <p>Loading...</p>
              </div>
            )}

            {!loading && (
              <div className={styles.step}>
                {/* Display selected award template (read-only) */}
                {award && award.awardTemplateId && (
                  <div className={styles.selectedTemplate}>
                    <div className={styles.templateIcon}>
                      <FaTrophy />
                    </div>
                    <div>
                      <h4>{award.awardTemplateId.awardName}</h4>
                      <p>{award.awardTemplateId.description}</p>
                    </div>
                  </div>
                )}

                {/* Event Selection */}
                <div className={styles.formGroupFull}>
                  <label className={styles.formLabel}>
                    <FaCalendarAlt className={styles.formLabelIcon} />
                    Event (Optional)
                  </label>
                  
                  <div className={`${styles.searchableDropdown} ${selectedEventId ? styles.hasSelection : ''}`}>
                    <div className={`${styles.dropdownHeader} ${selectedEventId ? styles.hasSelection : ''}`}>
                      <FaSearch className={styles.searchIcon} />
                      <input
                        type="text"
                        placeholder={isEventDropdownOpen ? "Search events..." : getSelectedEventDisplayText()}
                        value={eventSearchTerm}
                        onChange={handleEventSearchChange}
                        onFocus={handleEventSearchFocus}
                        onBlur={handleEventDropdownBlur}
                        className={`${styles.searchInput} ${selectedEventId ? styles.selectedInput : ''}`}
                        readOnly={!isEventDropdownOpen}
                      />
                      {selectedEventId && !isEventDropdownOpen && (
                        <div className={styles.selectionIndicator}>
                          <div className={styles.checkmark}>✓</div>
                        </div>
                      )}
                    </div>
                    
                    {isEventDropdownOpen && (
                      <div className={styles.dropdownContent}>
                        {/* Series Award Option */}
                        <div
                          className={`${styles.dropdownOption} ${selectedEventId === '' ? styles.selected : ''}`}
                          onClick={(e) => handleDropdownOptionClick('', e)}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          <div className={styles.optionContent}>
                            <span className={styles.optionTitle}>Series Award</span>
                            <span className={styles.optionSubtitle}>No specific event</span>
                          </div>
                        </div>

                        {filteredEvents.map((event) => {
                          const eventDate = new Date(event.dateTime?.start || event.date);
                          const formattedDate = eventDate.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          });
                          const eventType = event.type || 'Event';
                          const isSelected = selectedEventId === event._id;

                          return (
                            <div
                              key={event._id}
                              className={`${styles.dropdownOption} ${isSelected ? styles.selected : ''}`}
                              onClick={(e) => handleDropdownOptionClick(event._id, e)}
                              onMouseDown={(e) => e.preventDefault()}
                            >
                              <div className={styles.optionContent}>
                                <span className={styles.optionTitle}>{event.title}</span>
                                <span className={styles.optionSubtitle}>{formattedDate} - {eventType}</span>
                              </div>
                            </div>
                          );
                        })}
                        
                        {events.length > 0 && filteredEvents.length === 0 && eventSearchTerm && (
                          <div className={styles.noResults}>
                            <p>No events found matching "{eventSearchTerm}"</p>
                          </div>
                        )}
                        
                        {events.length === 0 && (
                          <div className={styles.noResults}>
                            <p>No events available for this program</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Player Selection */}
                <div className={styles.formGroupFull}>
                  <label className={styles.formLabel}>
                    <FaUsers className={styles.formLabelIcon} />
                    Select Recipients
                  </label>
                  
                  <div className={`${styles.searchableDropdown} ${selectedRecipients.length > 0 ? styles.hasSelection : ''}`}>
                    <div className={`${styles.dropdownHeader} ${selectedRecipients.length > 0 ? styles.hasSelection : ''}`}>
                      <FaSearch className={styles.searchIcon} />
                      <input
                        type="text"
                        placeholder={isPlayerDropdownOpen ? "Search players..." : `${selectedRecipients.length} player(s) selected - Click to select more`}
                        value={searchTerm}
                        onChange={handlePlayerSearchChange}
                        onFocus={handlePlayerSearchFocus}
                        onBlur={handlePlayerDropdownBlur}
                        className={`${styles.searchInput} ${selectedRecipients.length > 0 ? styles.selectedInput : ''}`}
                        readOnly={!isPlayerDropdownOpen}
                      />
                      {selectedRecipients.length > 0 && !isPlayerDropdownOpen && (
                        <div className={styles.selectionIndicator}>
                          <div className={styles.checkmark}>{selectedRecipients.length}</div>
                        </div>
                      )}
                    </div>
                    
                    {isPlayerDropdownOpen && (
                      <div className={styles.dropdownContent}>
                        {filteredPlayers.length === 0 ? (
                          <div className={styles.noResults}>
                            <p>{searchTerm ? `No players found matching "${searchTerm}"` : 'No players available'}</p>
                          </div>
                        ) : (
                          filteredPlayers.map((player) => {
                            const isSelected = selectedRecipients.some(p => p.userId === player.userId);
                            return (
                              <div
                                key={player.userId}
                                className={`${styles.dropdownOption} ${isSelected ? styles.selected : ''}`}
                                onClick={(e) => handlePlayerOptionClick(player, e)}
                                onMouseDown={(e) => e.preventDefault()}
                              >
                                <div className={styles.optionContent}>
                                  <span className={styles.optionTitle}>
                                    {isSelected && '✓ '}
                                    {player.fullName || `${player.firstName} ${player.lastName}`}
                                  </span>
                                  <span className={styles.optionSubtitle}>
                                    {player.teamName || 'No Team'} • {formatRole(player.roleKey)}
                                  </span>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Selected Recipients Display */}
                {selectedRecipients.length > 0 && (
                  <div className={styles.selectedRecipients}>
                    <h4>Selected Recipients ({selectedRecipients.length})</h4>
                    <div className={styles.selectedRecipientsList}>
                      {selectedRecipients.map((recipient, index) => (
                        <div key={index} className={styles.selectedRecipient}>
                          {recipient.playerName}
                          <button
                            onClick={() => handleRecipientToggle({ userId: recipient.userId })}
                            className={styles.removeButton}
                            type="button"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div className={styles.formGroupFull}>
                  <label className={styles.formLabel}>
                    <FaCalendarAlt className={styles.formLabelIcon} />
                    Notes (Optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any additional notes about this award..."
                    className={styles.notesTextarea}
                    rows={3}
                  />
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Fixed Button Container */}
        <div className={styles.buttonContainer}>
          <button
            className={styles.cancelButton}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className={styles.createButton}
            onClick={handleSubmit}
            disabled={selectedRecipients.length === 0 || loading}
            type="button"
          >
            {loading ? 'Updating Award...' : 'Update Award'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AwardEditModal;

